// client/src/coms/organisation/ResourcesPanel.js
// Shared resources: curated guides, rules, tutorials, references, downloads, event material and FAQs, in
// collections, with tags and featured items. Each resource can point at a library file, cloud file, chat
// attachment, channel, message, Canvas document or an HTTPS link. What a member can see is decided by the
// server (resource scope + access to the target); this view only shows what it is given.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGetJson, apiPostJson, apiPatchJson, apiDelete, serverPath } from './orgApi';
import { formatDate, ProviderBadge, Dialog, useConfirm, usePrompt, Spinner, EmptyState, ErrorNote, notify, openTarget } from './orgShared';
import { saveBookmark } from './bookmarkActions';

const CATEGORY_LABELS = { guide: 'Guide', rules: 'Rules', tutorial: 'Tutorial', reference: 'Reference', download: 'Download', event: 'Event', faq: 'FAQ', announcement: 'Announcement', other: 'Other' };
const SCOPE_LABELS = { server: 'Everyone in the server', roles: 'Selected roles', channel: 'People who can read a channel', admins: 'Administrators only' };

export default function ResourcesPanel({ server, initial }) {
  const serverKey = String(server.id);
  const base = serverPath(serverKey);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [collection, setCollection] = useState(0);
  const [category, setCategory] = useState('');
  const [tag, setTag] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(initial && initial.resource_id ? Number(initial.resource_id) : null);
  const [editing, setEditing] = useState(null);
  const [confirm, confirmEl] = useConfirm();
  const [prompt, promptEl] = usePrompt();

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const qs = new URLSearchParams({ ...(collection ? { collection: String(collection) } : {}), ...(category ? { category } : {}), ...(tag ? { tag } : {}), ...(q ? { q } : {}) });
      const r = await apiGetJson(`${base}/resources?${qs}`);
      if (mine !== seq.current) return;
      setData(r);
      setErr(null);
    } catch (e) {
      if (mine === seq.current) setErr(e);
    }
  }, [base, collection, category, tag, q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);
  useEffect(() => {
    const on = (e) => {
      const d = e.detail || {};
      if (d.area === 'library' && d.resource_id) setOpen(Number(d.resource_id));
    };
    window.addEventListener('vox-open-org', on);
    return () => window.removeEventListener('vox-open-org', on);
  }, []);

  async function newCollection() {
    const name = await prompt({ title: 'New collection', label: 'Name', confirmLabel: 'Create', placeholder: 'Game Rules' });
    if (!name) return;
    try {
      const r = await apiPostJson(`${base}/resource-collections`, { name });
      setCollection(r.collection_id);
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function renameCollection(c) {
    const name = await prompt({ title: 'Rename collection', label: 'Name', value: c.name });
    if (!name) return;
    await apiPatchJson(`${base}/resource-collections/${c.id}`, { name }).catch((e) => notify(e.message, { tone: 'error' }));
    load();
  }
  async function deleteCollection(c) {
    if (!(await confirm({ title: `Delete collection “${c.name}”?`, body: 'Its resources are kept (they just leave the collection).', confirmLabel: 'Delete collection', danger: true }))) return;
    await apiDelete(`${base}/resource-collections/${c.id}`).catch((e) => notify(e.message, { tone: 'error' }));
    setCollection(0);
    load();
  }
  async function removeResource(r) {
    if (!(await confirm({ title: `Delete “${r.title}”?`, body: 'Only the resource entry is deleted. Linked files, channels and messages are not changed.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await apiDelete(`${base}/resources/${r.id}`);
      setOpen(null);
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  const resources = (data && data.resources) || [];
  const featured = resources.filter((r) => r.featured);
  const canManage = data && data.can_manage;
  const filtering = !!(collection || category || tag || q);

  return (
    <div className="vxo-split" data-testid="resources-panel">
      <nav className="vxo-nav" aria-label="Resource collections">
        <button type="button" className={`vxo-nav-item${!collection ? ' is-active' : ''}`} onClick={() => setCollection(0)}>
          <span className="vxo-nav-icon" aria-hidden="true">
            📚
          </span>
          <span className="vxo-nav-text">
            <span className="vxo-nav-label">All resources</span>
          </span>
        </button>
        {((data && data.collections) || []).map((c) => (
          <div key={c.id} className="vxo-nav-row">
            <button type="button" className={`vxo-nav-item${collection === c.id ? ' is-active' : ''}`} onClick={() => setCollection(c.id)}>
              <span className="vxo-nav-icon" aria-hidden="true">
                🗂️
              </span>
              <span className="vxo-nav-text">
                <span className="vxo-nav-label">{c.name}</span>
              </span>
            </button>
            {canManage && collection === c.id ? (
              <span className="vxo-nav-tools">
                <button type="button" className="vx-icon-btn" aria-label={`Rename ${c.name}`} onClick={() => renameCollection(c)}>
                  ✎
                </button>
                <button type="button" className="vx-icon-btn" aria-label={`Delete ${c.name}`} onClick={() => deleteCollection(c)}>
                  🗑
                </button>
              </span>
            ) : null}
          </div>
        ))}
        {canManage ? (
          <button type="button" className="vx-btn vxo-connect-btn" onClick={newCollection}>
            ＋ New collection
          </button>
        ) : null}
        {data && data.tags.length ? (
          <div className="vxo-tags" aria-label="Tags">
            {data.tags.slice(0, 30).map((t) => (
              <button key={t.tag} type="button" className={`vxo-tag${tag === t.tag ? ' is-on' : ''}`} aria-pressed={tag === t.tag} onClick={() => setTag(tag === t.tag ? '' : t.tag)}>
                #{t.tag} <span className="vxo-muted">{t.count}</span>
              </button>
            ))}
          </div>
        ) : null}
      </nav>
      <section className="vxo-main">
        <div className="vxo-toolbar">
          <div className="vxo-toolbar-actions">
            <input className="vxo-input" type="search" placeholder="Search resources" aria-label="Search resources" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="vxo-select" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            {canManage ? (
              <button type="button" className="vx-btn primary" onClick={() => setEditing({ collection_id: collection || null })} data-testid="resource-new">
                ＋ Add resource
              </button>
            ) : null}
          </div>
        </div>
        <ErrorNote error={err} onRetry={load} />
        {!data && !err ? <Spinner /> : null}
        {data && !filtering && featured.length ? (
          <>
            <h2 className="vxo-h3">Featured</h2>
            <div className="vxo-cards">{featured.map((r) => <ResourceCard key={r.id} r={r} onOpen={() => setOpen(r.id)} />)}</div>
          </>
        ) : null}
        {data && !filtering && data.recent.length ? (
          <div className="vxo-recent-row">
            <div className="vxo-recent-title">Recently updated</div>
            <div className="vxo-recent-items">
              {data.recent.map((r) => (
                <button key={r.id} type="button" className="vxo-chip" onClick={() => setOpen(r.id)}>
                  <span className="vxo-chip-text">{r.title}</span> <span className="vxo-muted">{formatDate(r.updated_at, { time: false })}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {data && !resources.length ? (
          <EmptyState icon="📚" title={filtering ? 'No resources match' : 'No resources yet'}>
            {canManage ? 'Add guides, rules, FAQs, downloads and links so members can find them without scrolling through chat.' : 'Moderators can add guides, rules and downloads here.'}
          </EmptyState>
        ) : null}
        {data && resources.length ? (
          <>
            <h2 className="vxo-h3">{filtering ? 'Results' : 'All resources'}</h2>
            <div className="vxo-cards">{resources.map((r) => <ResourceCard key={r.id} r={r} onOpen={() => setOpen(r.id)} />)}</div>
          </>
        ) : null}
      </section>
      {open ? <ResourceDetail base={base} id={open} onClose={() => setOpen(null)} onEdit={(r) => setEditing(r)} onDelete={removeResource} /> : null}
      {editing ? <ResourceEditor base={base} server={server} serverKey={serverKey} collections={(data && data.collections) || []} initial={editing} onClose={() => setEditing(null)} onSaved={() => {
        setEditing(null);
        load();
      }} /> : null}
      {confirmEl}
      {promptEl}
    </div>
  );
}

function targetIcon(r) {
  return { file: '📄', external: '☁️', attachment: '📎', channel: '#', message: '💬', canvas: '🎨', link: '🔗', none: '📝' }[r.target_type] || '📝';
}

function ResourceCard({ r, onOpen }) {
  return (
    <button type="button" className="vxo-res-card" onClick={onOpen} data-testid="resource-card">
      <span className="vxo-res-top">
        <span className="vxo-badge">{CATEGORY_LABELS[r.category] || r.category}</span>
        {r.featured ? <span className="vxo-badge is-accent">Featured</span> : null}
        {r.scope !== 'server' ? <span className="vxo-badge">{r.scope === 'admins' ? 'Admins' : 'Restricted'}</span> : null}
      </span>
      <span className="vxo-res-title">
        <span aria-hidden="true">{targetIcon(r)}</span> {r.title}
      </span>
      {r.description ? <span className="vxo-res-desc">{r.description}</span> : null}
      <span className="vxo-res-foot">
        {r.target && r.target.provider ? <ProviderBadge provider={r.target.provider} /> : null}
        {r.target && r.target.status !== 'ok' ? <span className="vxo-warn-text">{r.target.status === 'missing' ? 'Link broken' : 'Not available to you'}</span> : null}
        {r.tags.slice(0, 4).map((t) => (
          <span key={t} className="vxo-muted">
            #{t}
          </span>
        ))}
        <span className="vxo-muted">{formatDate(r.updated_at, { time: false })}</span>
      </span>
    </button>
  );
}

function ResourceDetail({ base, id, onClose, onEdit, onDelete }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiGetJson(`${base}/resources/${id}`).then((r) => setD(r.resource), setErr);
  }, [base, id]);
  const t = d && d.target;
  return (
    <Dialog title={d ? d.title : 'Resource'} onClose={onClose} wide>
      <ErrorNote error={err} />
      {!d && !err ? <Spinner /> : null}
      {d ? (
        <div className="vxo-res-detail">
          <div className="vxo-res-top">
            <span className="vxo-badge">{CATEGORY_LABELS[d.category]}</span>
            {d.tags.map((x) => (
              <span key={x} className="vxo-muted">
                #{x}
              </span>
            ))}
            <span className="vxo-muted">Updated {formatDate(d.updated_at)}</span>
            <span className="vxo-muted">· {SCOPE_LABELS[d.scope]}</span>
          </div>
          {d.description ? <p className="vxo-res-desc-full">{d.description}</p> : null}
          {d.body ? <div className="vxo-res-body">{d.body}</div> : null}
          {t && d.target_type !== 'none' ? (
            <div className="vxo-card">
              {t.status === 'ok' ? (
                <>
                  <div>
                    {t.provider ? <ProviderBadge provider={t.provider} withLabel /> : null} <strong>{t.label}</strong> {t.archived ? <span className="vxo-badge">archived</span> : null}
                  </div>
                  <div className="vxo-actions">
                    {t.open ? (
                      <button type="button" className="vx-btn primary" onClick={() => {
                        openTarget(t.open);
                        onClose();
                      }}>
                        Open
                      </button>
                    ) : null}
                    {t.download_url ? (
                      <a className="vx-btn" href={t.download_url}>
                        Download
                      </a>
                    ) : null}
                  </div>
                </>
              ) : (
                <p className="vxo-warn-text">
                  {t.status === 'missing' ? 'The linked item no longer exists.' : t.status === 'disconnected' ? 'The linked storage was disconnected.' : 'You don’t have access to the linked item. Ask a moderator or request access in the storage provider.'}
                </p>
              )}
            </div>
          ) : null}
          {d.related_channel ? (
            <p>
              Discussion:{' '}
              <button type="button" className="vxo-linkbtn" onClick={() => {
                openTarget({ kind: 'channel', server_key: base.split('/').pop(), channel_kind: 'text', channel_id: d.related_channel.id });
                onClose();
              }}>
                #{d.related_channel.name}
              </button>
            </p>
          ) : null}
          <div className="vxo-actions">
            <button type="button" className="vx-btn" onClick={() => saveBookmark('resource', d.id)}>
              🔖 Save bookmark
            </button>
            {d.can_edit ? (
              <>
                <button type="button" className="vx-btn" onClick={() => {
                  onEdit(d);
                  onClose();
                }}>
                  Edit
                </button>
                <button type="button" className="vx-btn danger" onClick={() => onDelete(d)}>
                  Delete
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

function ResourceEditor({ base, server, serverKey, collections, initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [f, setF] = useState(() => ({
    title: initial.title || '',
    description: initial.description || '',
    body: initial.body || '',
    category: initial.category || 'guide',
    collection_id: initial.collection_id || '',
    tags: (initial.tags || []).join(', '),
    featured: !!initial.featured,
    scope: initial.scope || 'server',
    scope_role_ids: initial.scope_role_ids || [],
    scope_channel_id: initial.scope_channel_id || '',
    related_channel_id: initial.related_channel_id || '',
    target_type: initial.target_type || 'none',
    target_ref: initial.target_ref || '',
    target_label: '',
    url: initial.url || '',
  }));
  const [roles, setRoles] = useState([]);
  const [pick, setPick] = useState('');
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const textChannels = useMemo(() => (server.channels || []).filter((c) => c.kind === 'text'), [server]);
  useEffect(() => {
    apiGetJson(`/servers/${encodeURIComponent(serverKey)}/roles`).then((r) => setRoles((r.roles || r || []).filter((x) => !Number(x.is_default))), () => setRoles([]));
  }, [serverKey]);
  // Pick a file from this server's libraries (internal, attachments and indexed cloud files) via search.
  useEffect(() => {
    if (!['file', 'external', 'attachment'].includes(f.target_type) || !pick.trim()) {
      setFound([]);
      return undefined;
    }
    const t = setTimeout(() => {
      apiGetJson(`/search?q=${encodeURIComponent(pick.trim())}&category=files&scope=server&server=${encodeURIComponent(serverKey)}`).then((r) => setFound(r.results || []), () => setFound([]));
    }, 300);
    return () => clearTimeout(t);
  }, [pick, f.target_type, serverKey]);
  async function save() {
    setBusy(true);
    setErr(null);
    const body = {
      title: f.title,
      description: f.description,
      body: f.body,
      category: f.category,
      collection_id: f.collection_id ? Number(f.collection_id) : null,
      tags: f.tags.split(',').map((x) => x.trim()).filter(Boolean),
      featured: f.featured,
      scope: f.scope,
      scope_role_ids: f.scope_role_ids,
      scope_channel_id: f.scope_channel_id ? Number(f.scope_channel_id) : null,
      related_channel_id: f.related_channel_id ? Number(f.related_channel_id) : null,
      target_type: f.target_type,
      target_ref: f.target_type === 'channel' ? String(f.target_ref) : f.target_ref,
      url: f.url,
    };
    try {
      if (editing) await apiPatchJson(`${base}/resources/${initial.id}`, body);
      else await apiPostJson(`${base}/resources`, body);
      notify(editing ? 'Resource saved' : 'Resource added', { tone: 'success' });
      onSaved();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={editing ? 'Edit resource' : 'Add resource'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" disabled={busy || !f.title.trim()} onClick={save} data-testid="resource-save">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <ErrorNote error={err} />
      <div className="vxo-form">
        <label className="vxo-field">
          <span>Title</span>
          <input value={f.title} maxLength={120} onChange={(e) => set({ title: e.target.value })} data-testid="resource-title" />
        </label>
        <div className="vxo-row2">
          <label className="vxo-field">
            <span>Category</span>
            <select value={f.category} onChange={(e) => set({ category: e.target.value })}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="vxo-field">
            <span>Collection</span>
            <select value={f.collection_id} onChange={(e) => set({ collection_id: e.target.value })}>
              <option value="">None</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="vxo-field">
          <span>Short description</span>
          <input value={f.description} maxLength={2000} onChange={(e) => set({ description: e.target.value })} />
        </label>
        <label className="vxo-field">
          <span>Text (optional: rules, FAQ answers, instructions)</span>
          <textarea rows={5} value={f.body} maxLength={20000} onChange={(e) => set({ body: e.target.value })} />
        </label>
        <fieldset className="vxo-fieldset">
          <legend>Points to</legend>
          <div className="vxo-seg-row" role="radiogroup">
            {[
              ['none', 'Nothing'],
              ['file', 'Library file'],
              ['external', 'Cloud file'],
              ['attachment', 'Chat attachment'],
              ['channel', 'Channel'],
              ['link', 'Web link'],
            ].map(([k, v]) => (
              <label key={k} className="vxo-radio inline">
                <input type="radio" name="target_type" checked={f.target_type === k} onChange={() => set({ target_type: k, target_ref: '', target_label: '' })} />
                <span>{v}</span>
              </label>
            ))}
          </div>
          {f.target_type === 'link' ? (
            <label className="vxo-field">
              <span>HTTPS address</span>
              <input value={f.url} placeholder="https://" onChange={(e) => set({ url: e.target.value })} />
            </label>
          ) : null}
          {f.target_type === 'channel' ? (
            <label className="vxo-field">
              <span>Channel</span>
              <select value={f.target_ref} onChange={(e) => set({ target_ref: e.target.value })}>
                <option value="">Choose…</option>
                {textChannels.map((c) => (
                  <option key={c.id} value={c.db_id != null ? c.db_id : c.id}>
                    #{c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {['file', 'external', 'attachment'].includes(f.target_type) ? (
            <>
              <label className="vxo-field">
                <span>Find a file {f.target_ref ? `(selected: ${f.target_label || `#${f.target_ref}`})` : ''}</span>
                <input value={pick} placeholder="Type part of the file name" onChange={(e) => setPick(e.target.value)} />
              </label>
              <ul className="vxo-pick">
                {found
                  .filter((x) => x.type === f.target_type)
                  .slice(0, 8)
                  .map((x) => (
                    <li key={`${x.type}${x.id}`}>
                      <button type="button" className={`vxo-linkbtn${String(f.target_ref) === String(x.id) ? ' is-on' : ''}`} onClick={() => set({ target_ref: String(x.id), target_label: x.title })}>
                        {x.provider ? <ProviderBadge provider={x.provider} /> : null} {x.title} <span className="vxo-muted">{x.source}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            </>
          ) : null}
        </fieldset>
        <fieldset className="vxo-fieldset">
          <legend>Who can see it</legend>
          {Object.entries(SCOPE_LABELS).map(([k, v]) => (
            <label key={k} className="vxo-radio">
              <input type="radio" name="scope" checked={f.scope === k} onChange={() => set({ scope: k })} />
              <span>{v}</span>
            </label>
          ))}
          {f.scope === 'roles' ? (
            <div className="vxo-checks">
              {roles.map((r) => (
                <label key={r.id} className="vxo-check">
                  <input type="checkbox" checked={f.scope_role_ids.includes(Number(r.id))} onChange={(e) => set({ scope_role_ids: e.target.checked ? [...f.scope_role_ids, Number(r.id)] : f.scope_role_ids.filter((x) => x !== Number(r.id)) })} />
                  <span>{r.name}</span>
                </label>
              ))}
            </div>
          ) : null}
          {f.scope === 'channel' ? (
            <select className="vxo-select" value={f.scope_channel_id} onChange={(e) => set({ scope_channel_id: e.target.value })} aria-label="Channel whose readers can see this">
              <option value="">Choose a channel…</option>
              {textChannels.map((c) => (
                <option key={c.id} value={c.db_id != null ? c.db_id : c.id}>
                  #{c.name}
                </option>
              ))}
            </select>
          ) : null}
          <p className="vxo-hint">A resource never grants access to what it links: people who cannot open the linked item see that it is unavailable.</p>
        </fieldset>
        <div className="vxo-row2">
          <label className="vxo-field">
            <span>Tags (comma separated)</span>
            <input value={f.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="raids, beginners" />
          </label>
          <label className="vxo-field">
            <span>Related discussion</span>
            <select value={f.related_channel_id} onChange={(e) => set({ related_channel_id: e.target.value })}>
              <option value="">None</option>
              {textChannels.map((c) => (
                <option key={c.id} value={c.db_id != null ? c.db_id : c.id}>
                  #{c.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="vxo-check">
          <input type="checkbox" checked={f.featured} onChange={(e) => set({ featured: e.target.checked })} />
          <span>Feature this resource</span>
        </label>
      </div>
    </Dialog>
  );
}
